import React, { useEffect, useState } from 'react';
import Spinner from 'sushiweb/Spinner/Spinner';
import red from 'sushiweb/tokens/color/red';
import CheckCircleFill from 'sushiweb/Icons/all/CheckCircleFill';
import green from 'sushiweb/tokens/color/green';
import ExclamationLine from 'sushiweb/Icons/all/ExclamationLine';
import grey from 'sushiweb/tokens/color/grey';
import CrossCircle from 'sushiweb/Icons/all/CrossCircle';
import Textarea from 'sushiweb/InputTypes/Textarea';
import Button from 'sushiweb/Button/Button';
import CrossCircleFill from 'sushiweb/Icons/all/CrossCircleFill';
import UnlockHistory from 'sushiweb/Icons/all/UnlockHistory';
import { useLocation, matchPath, useParams } from 'react-router-dom';
import {
  expenseRequestAction,
  orderExpenseRequestAction,
} from '../../../api/expenseRequest';
import {
  expenseRequestActionErrorCode,
  expenseRequestStatusEnum,
} from './constants';
import ErrorPage from '../../common/components/ErrorPage';
import ActionResponse from './ActionResponse';
import {
  ButtonWrapper,
  LoggedOutStateWrapper,
  SpinnerHolder,
} from '../../common/styles';

import { InputWrapper } from './styles';
import { clearCookie } from '../../../helpers';
import zfetch, { METHOD_POST } from '../../common/utils/zfetch';
import { GET_AUTH_VALIDATE } from '../../../constants/urls';
import {
  LOGGED_OUT_EXPENSE_REQUEST_ACTION_ROUTE,
  LOGGED_OUT_ORDER_EXPENSE_REQUEST_ACTION_ROUTE,
} from '../../../constants/routes';

const ExpenseActionPage = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [errorCode, setErrorCode] = useState('');
  const [
    expenseRequestActionResponse,
    setExpenseRequestActionResponse,
  ] = useState({});
  const { expenseRequestPayload } = useParams();
  const [actionReason, setActionReason] = useState('');
  const [isAuthCalled, setIsAuthCalled] = useState(false);

  const isOrderExpenseRoute = matchPath(
    LOGGED_OUT_ORDER_EXPENSE_REQUEST_ACTION_ROUTE,
    location.pathname,
  );

  const isExpenseRoute = matchPath(
    LOGGED_OUT_EXPENSE_REQUEST_ACTION_ROUTE,
    location.pathname,
  );

  useEffect(() => {
    clearCookie('__Host-csrft').then(
      zfetch(GET_AUTH_VALIDATE, {
        method: METHOD_POST,
      })
        .then(res => res.json())
        .then(data => {
          // can check for logged in state here
          console.log(data);
          setIsAuthCalled(true);
        })
        .catch(err => {
          console.error(err);
          setIsAuthCalled(true);
        }),
    );
  }, []);

  const handleExpenseRequestAction = () => {
    setLoading(true);

    let action;

    if (isOrderExpenseRoute) {
      action = orderExpenseRequestAction({
        encrypted_payload: expenseRequestPayload,
        action_reason: actionReason,
      });
    } else if (isExpenseRoute) {
      action = expenseRequestAction({
        encrypted_payload: expenseRequestPayload,
        action_reason: actionReason,
      });
    } else {
      setLoading(false);
      return;
    }

    action
      .then(res => {
        setErrorCode(res?.error_code);
        setExpenseRequestActionResponse(res?.data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSubmit = () => {
    handleExpenseRequestAction();
  };

  useEffect(() => {
    if (isAuthCalled) {
      handleExpenseRequestAction();
    }
  }, [isAuthCalled]);

  const updateReason = val => {
    setActionReason(val);
  };

  const renderContent = () => {
    if (errorCode !== '0' && errorCode !== '') {
      switch (errorCode) {
        case expenseRequestActionErrorCode.RESPONSE_ALREADY_CAPTURED:
          return (
            <>
              <ExclamationLine
                color={grey.z500}
                size='14rem'
                style={{ padding: '10px 30px 30px 30px' }}
              />
              <ActionResponse
                title='Action already taken'
                comment="You've already submitted a response to this request. No further action is needed."
              />
            </>
          );
        case expenseRequestActionErrorCode.MISSING_REJECTION_REASON:
          return (
            <>
              <CrossCircle
                color={red.z500}
                size='10rem'
                style={{ padding: '10px 30px 30px 30px' }}
              />
              <ActionResponse
                title='Are you sure?'
                comment={expenseRequestActionResponse?.comment}
              />
              <InputWrapper>
                <Textarea
                  label='Enter reason'
                  value={actionReason}
                  onChange={updateReason}
                  placeholder='Enter reason'
                  innerWidth='40rem'
                />
              </InputWrapper>
              <ButtonWrapper>
                <Button onClick={handleSubmit}>Confirm</Button>
              </ButtonWrapper>
            </>
          );
        case expenseRequestActionErrorCode.REQUEST_EXPIRED:
          return (
            <>
              <UnlockHistory
                color={grey.z500}
                size='10rem'
                style={{ padding: '10px 30px 30px 30px' }}
              />
              <ActionResponse
                title='Uh oh, request expired!'
                comment='It is not possible to action on this request anymore. The requester has been notified and may contact you directly'
                actionReason={expenseRequestActionResponse.action_reason}
              />
            </>
          );
        default:
          return <ErrorPage errorMsg='Invalid Action Performed' />;
      }
    }
    switch (expenseRequestActionResponse?.status) {
      case expenseRequestStatusEnum.APPROVED:
        return (
          <LoggedOutStateWrapper>
            <CheckCircleFill
              color={green.z500}
              size='10rem'
              style={{ padding: '10px 30px 30px 30px' }}
            />
            <ActionResponse
              title={
                isExpenseRoute
                  ? 'Budget request approved!'
                  : 'Dining expense request approved!'
              }
              comment={expenseRequestActionResponse?.comment}
            />
          </LoggedOutStateWrapper>
        );

      case expenseRequestStatusEnum.REJECTED:
        return (
          <>
            <CrossCircleFill
              color={red.z500}
              size='10rem'
              style={{ padding: '10px 30px 30px 30px' }}
            />
            <ActionResponse
              title={
                isExpenseRoute
                  ? 'Budget request declined'
                  : 'Dining request declined'
              }
              comment={expenseRequestActionResponse?.comment}
              actionReason={expenseRequestActionResponse.action_reason}
            />
          </>
        );

      case expenseRequestStatusEnum.EXPIRED:
        return (
          <>
            <UnlockHistory
              color={grey.z500}
              size='10rem'
              style={{ padding: '10px 30px 30px 30px' }}
            />
            <ActionResponse
              title='Uh oh, request expired!'
              comment='It is not possible to action on this request anymore. The requester has been notified and may contact you directly'
              actionReason={expenseRequestActionResponse.action_reason}
            />
          </>
        );

      default:
        return <ErrorPage errorMsg='Invalid Action Performed' />;
    }
  };

  return (
    <>
      <LoggedOutStateWrapper>
        {loading ? (
          <>
            <LoggedOutStateWrapper>
              <SpinnerHolder className='center'>
                <Spinner color={red.z_red} size='medium' />
              </SpinnerHolder>
            </LoggedOutStateWrapper>
          </>
        ) : (
          renderContent()
        )}
      </LoggedOutStateWrapper>
    </>
  );
};

export default ExpenseActionPage;
